import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const ZodiacNumerologyCalculator = () => {
  const [date, setDate] = useState('');
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  // Zodiac Sign Meanings
  const zodiacSigns = {
    Aries: {
      power: 'Pioneering Power',
      traits: [
        'Bold',
        'Energetic',
        'Independent',
        'Courageous',
        'Competitive'
      ],
      description: 'Aries represents pioneering spirit and leadership. People born under Aries are often bold, energetic, and have a strong sense of independence.',
      dates: 'March 21 - April 19'
    },
    Taurus: {
      power: 'Stability Power',
      traits: [
        'Patient',
        'Determined',
        'Reliable',
        'Practical',
        'Resourceful'
      ],
      description: 'Taurus represents stability and practicality. People born under Taurus are often patient, determined, and have strong organizational skills.',
      dates: 'April 20 - May 20'
    },
    Gemini: {
      power: 'Communication Power',
      traits: [
        'Adaptable',
        'Expressive',
        'Intelligent',
        'Curious',
        'Versatile'
      ],
      description: 'Gemini represents communication and adaptability. People born under Gemini are often adaptable, expressive, and have strong communication skills.',
      dates: 'May 21 - June 20'
    },
    Cancer: {
      power: 'Emotional Power',
      traits: [
        'Sensitive',
        'Intuitive',
        'Caring',
        'Protective',
        'Loyal'
      ],
      description: 'Cancer represents emotional depth and intuition. People born under Cancer are often sensitive, intuitive, and have strong emotional bonds.',
      dates: 'June 21 - July 22'
    },
    Leo: {
      power: 'Leadership Power',
      traits: [
        'Confident',
        'Creative',
        'Generous',
        'Ambitious',
        'Charismatic'
      ],
      description: 'Leo represents leadership and creativity. People born under Leo are often confident, creative, and have strong leadership qualities.',
      dates: 'July 23 - August 22'
    },
    Virgo: {
      power: 'Analytical Power',
      traits: [
        'Analytical',
        'Practical',
        'Organized',
        'Detail-oriented',
        'Perfectionist'
      ],
      description: 'Virgo represents analytical thinking and practicality. People born under Virgo are often analytical, practical, and have strong organizational skills.',
      dates: 'August 23 - September 22'
    },
    Libra: {
      power: 'Balance Power',
      traits: [
        'Harmonious',
        'Diplomatic',
        'Social',
        'Fair',
        'Cooperative'
      ],
      description: 'Libra represents balance and harmony. People born under Libra are often harmonious, diplomatic, and have strong social skills.',
      dates: 'September 23 - October 22'
    },
    Scorpio: {
      power: 'Transformation Power',
      traits: [
        'Intense',
        'Passionate',
        'Resourceful',
        'Magnetic',
        'Mysterious'
      ],
      description: 'Scorpio represents transformation and intensity. People born under Scorpio are often intense, passionate, and have strong transformational qualities.',
      dates: 'October 23 - November 21'
    },
    Sagittarius: {
      power: 'Adventure Power',
      traits: [
        'Adventurous',
        'Optimistic',
        'Independent',
        'Philosophical',
        'Honest'
      ],
      description: 'Sagittarius represents adventure and optimism. People born under Sagittarius are often adventurous, optimistic, and have strong philosophical thinking.',
      dates: 'November 22 - December 21'
    },
    Capricorn: {
      power: 'Ambition Power',
      traits: [
        'Ambitious',
        'Disciplined',
        'Practical',
        'Patient',
        'Determined'
      ],
      description: 'Capricorn represents ambition and practicality. People born under Capricorn are often ambitious, disciplined, and have strong practical skills.',
      dates: 'December 22 - January 19'
    },
    Aquarius: {
      power: 'Innovation Power',
      traits: [
        'Innovative',
        'Independent',
        'Intellectual',
        'Humanitarian',
        'Progressive'
      ],
      description: 'Aquarius represents innovation and progress. People born under Aquarius are often innovative, independent, and have strong intellectual capabilities.',
      dates: 'January 20 - February 18'
    },
    Pisces: {
      power: 'Intuition Power',
      traits: [
        'Intuitive',
        'Compassionate',
        'Creative',
        'Sensitive',
        'Imaginative'
      ],
      description: 'Pisces represents intuition and creativity. People born under Pisces are often intuitive, compassionate, and have strong creative abilities.',
      dates: 'February 19 - March 20'
    }
  };

  const handleDateChange = (e) => {
    setDate(e.target.value);
    setError('');
  };

  const calculateZodiacPower = () => {
    if (!date) {
      setError('Please enter your birth date');
      return;
    }

    const birthDate = new Date(date);
    const month = birthDate.getMonth() + 1;
    const day = birthDate.getDate();

    let zodiacSign = '';

    if ((month === 3 && day >= 21) || (month === 4 && day <= 19)) {
      zodiacSign = 'Aries';
    } else if ((month === 4 && day >= 20) || (month === 5 && day <= 20)) {
      zodiacSign = 'Taurus';
    } else if ((month === 5 && day >= 21) || (month === 6 && day <= 20)) {
      zodiacSign = 'Gemini';
    } else if ((month === 6 && day >= 21) || (month === 7 && day <= 22)) {
      zodiacSign = 'Cancer';
    } else if ((month === 7 && day >= 23) || (month === 8 && day <= 22)) {
      zodiacSign = 'Leo';
    } else if ((month === 8 && day >= 23) || (month === 9 && day <= 22)) {
      zodiacSign = 'Virgo';
    } else if ((month === 9 && day >= 23) || (month === 10 && day <= 22)) {
      zodiacSign = 'Libra';
    } else if ((month === 10 && day >= 23) || (month === 11 && day <= 21)) {
      zodiacSign = 'Scorpio';
    } else if ((month === 11 && day >= 22) || (month === 12 && day <= 21)) {
      zodiacSign = 'Sagittarius';
    } else if ((month === 12 && day >= 22) || (month === 1 && day <= 19)) {
      zodiacSign = 'Capricorn';
    } else if ((month === 1 && day >= 20) || (month === 2 && day <= 18)) {
      zodiacSign = 'Aquarius';
    } else {
      zodiacSign = 'Pisces';
    }

    const zodiacData = zodiacSigns[zodiacSign];
    setResults({
      zodiacSign,
      power: zodiacData.power,
      traits: zodiacData.traits,
      description: zodiacData.description,
      dates: zodiacData.dates
    });

    // Prepare chart data
    const zodiacChartData = {
      labels: ['Power', 'Traits'],
      datasets: [{
        label: 'Zodiac Power Distribution',
        data: [70, 30],
        backgroundColor: [
          'rgba(153, 102, 255, 0.7)',
          'rgba(255, 159, 64, 0.7)'
        ],
        borderColor: [
          'rgba(153, 102, 255, 1)',
          'rgba(255, 159, 64, 1)'
        ],
        borderWidth: 1,
        hoverOffset: 4
      }]
    };

    setChartData(zodiacChartData);
  };

  return (
    <div className="math-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Helmet>
        <title>Zodiac Numerology Calculator | Calculate Your Zodiac Power</title>
        <meta name="description" content="Calculate the numerological significance of your zodiac sign using our Astro-Numerology Calculator. Understand your personality traits and potential." />
        <meta name="keywords" content="zodiac numerology, astro-numerology calculator, personality traits, zodiac analysis, birth date calculator" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://yourwebsite.com/calculator/zodiac-numerology" />
        <meta property="og:title" content="Zodiac Numerology Calculator | Calculate Your Zodiac Power" />
        <meta property="og:description" content="Calculate the numerological significance of your zodiac sign using our Astro-Numerology Calculator. Understand your personality traits and potential." />
        <meta property="og:image" content="https://yourwebsite.com/images/zodiac-numerology-calculator.png" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://yourwebsite.com/calculator/zodiac-numerology" />
        <meta property="twitter:title" content="Zodiac Numerology Calculator | Calculate Your Zodiac Power" />
        <meta property="twitter:description" content="Calculate the numerological significance of your zodiac sign using our Astro-Numerology Calculator. Understand your personality traits and potential." />
        <meta property="twitter:image" content="https://yourwebsite.com/images/zodiac-numerology-calculator.png" />
      </Helmet>

      <div className="calculator-header mb-12">
        <div className="flex items-center gap-4">
          <div className="header-icon text-4xl">💫</div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Zodiac Numerology Calculator</h1>
            <p className="text-lg text-purple-600 font-semibold">Calculate Your Zodiac Power</p>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
      >
        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="date">
            Your Birth Date:
          </label>
          <input
            type="date"
            id="date"
            name="date"
            value={date}
            onChange={handleDateChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
          />
        </div>

        <button
          onClick={calculateZodiacPower}
          className="w-full bg-purple-500 hover:bg-purple-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white"
        >
          Calculate Zodiac Power
        </button>

        {error && (
          <div className="text-red-500 text-center mt-4">
            {error}
          </div>
        )}

        {results && (
          <div className="results-section mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Your Zodiac Power</h3>
            
            <div className="space-y-12">
              <div className="bg-white rounded-xl shadow-lg p-8">
                <div className="space-y-8">
                  <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Zodiac Sign:</span>
                      <span className="font-medium text-purple-600 text-2xl">{results.zodiacSign}</span>
                    </div>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                    <h5 className="font-medium mb-4">Power Type:</h5>
                    <p className="text-purple-600 text-xl">{results.power}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                    <h5 className="font-medium mb-4">Key Traits:</h5>
                    <ul className="list-disc pl-6 space-y-2">
                      {results.traits.map((trait, index) => (
                        <li key={index} className="text-gray-700">{trait}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                    <h5 className="font-medium mb-4">Description:</h5>
                    <p className="text-gray-700">{results.description}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                    <h5 className="font-medium mb-4">Date Range:</h5>
                    <p className="text-gray-700">{results.dates}</p>
                  </div>
                </div>
              </div>

              {/* Power Visualization Section */}
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h4 className="text-xl font-semibold mb-6">Power Visualization</h4>
                <div className="h-96 bg-gray-50 rounded-xl p-6">
                  <Doughnut
                    data={chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        title: {
                          display: true,
                          text: 'Zodiac Power Distribution',
                          color: '#4F46E5',
                          font: {
                            size: 16
                          }
                        },
                        legend: {
                          display: true,
                          position: 'bottom',
                          labels: {
                            color: '#4B5563',
                            font: {
                              size: 12
                            }
                          }
                        }
                      },
                      cutout: '60%',
                      animation: {
                        animateRotate: true,
                        animateScale: true
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </motion.div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Examples & Interpretations <span className="ml-2 text-2xl">📊</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Example 1: Leo's Leadership Power</h3>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="font-medium mb-2">Birth Date: July 28</p>
                <p className="text-purple-600 text-2xl mb-2">Zodiac: Leo</p>
                <p className="mt-4 text-gray-700">
                  Interpretation: This birth date indicates strong leadership qualities, creativity, and charisma.
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Example 2: Cancer's Emotional Power</h3>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="font-medium mb-2">Birth Date: June 15</p>
                <p className="text-purple-600 text-2xl mb-2">Zodiac: Cancer</p>
                <p className="mt-4 text-gray-700">
                  Interpretation: This birth date suggests strong emotional depth, intuition, and nurturing qualities.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          How to Use <span className="ml-2 text-2xl">📋</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Follow these steps to calculate your zodiac numerology:
          </p>
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 1: Enter Your Birth Date</h3>
              <p className="text-gray-700">
                Select your birth date from the date picker.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 2: Calculate</h3>
              <p className="text-gray-700">
                Click the "Calculate Zodiac Power" button to see your results.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 3: Understand Results</h3>
              <p className="text-gray-700">
                Review your zodiac sign's power type, key traits, and description to understand your personality potential.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          FAQ <span className="ml-2 text-2xl">❓</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-semibold mb-2">What is Zodiac Numerology?</h3>
              <p className="text-gray-700">
                Zodiac Numerology combines astrology and numerology to analyze the significance of your zodiac sign and its influence on your personality and life path.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">How is it calculated?</h3>
              <p className="text-gray-700">
                The calculator determines your zodiac sign based on your birth date and matches it with its corresponding numerological meanings, traits, and descriptions.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">What does it reveal?</h3>
              <p className="text-gray-700">
                It shows your zodiac sign's power type, key traits, strengths, challenges, and potential based on numerological principles.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Features & Benefits <span className="ml-2 text-2xl">✨</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Key Features</h3>
              <ul className="list-disc pl-8 space-y-3">
                <li>Zodiac sign identification</li>
                <li>Power type analysis</li>
                <li>Personality trait assessment</li>
                <li>Comprehensive descriptions</li>
                <li>Detailed interpretations</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Benefits</h3>
              <ul className="list-disc pl-8 space-y-3">
                <li>Understand personality traits</li>
                <li>Make informed decisions</li>
                <li>Enhance self-awareness</li>
                <li>Support personal growth</li>
                <li>Improve relationships</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ZodiacNumerologyCalculator;